import type { Translate, VrStateMsg } from "./types";

/**
 * Scene/step to the wording the design uses.
 *
 * These are plain functions rather than hooks, so the caller passes its own `t` in —
 * every call site already has one from useTranslation, and keeping the mapping out of
 * a component means the step ranges stay in one place.
 */

/** The phase key for a Scene04 step, or null outside 1..13. */
function phaseKey(step: number): string | null {
  if (step >= 1 && step <= 2) return "HeadsetDock.PhaseIntro";
  if (step === 3) return "HeadsetDock.PhasePressStart";
  if (step >= 4 && step <= 6) return "HeadsetDock.PhaseMeasurementIntro";
  if (step === 7) return "HeadsetDock.PhaseMeasurement";
  if (step === 8) return "HeadsetDock.PhaseMeasurementEnd";
  if (step >= 9 && step <= 13) return "HeadsetDock.PhaseEnd";
  return null;
}

/** The scene/step label shown in the bar, as the design spells it. */
export function mapWebState(t: Translate, s?: VrStateMsg): string {
  if (!s) return "-";

  if (s.settingsOpen) return t("HeadsetDock.SceneSettingsOpen");

  if (s.scene === 3) return t("HeadsetDock.SceneIdle");
  if (s.scene === 5) return t("HeadsetDock.SceneSettingsOpen");

  if (s.scene === 4) {
    // Scene04 names its own step, so the phase wording is shared with the progress
    // strip below instead of being spelled out a second time.
    const key = phaseKey(s.step ?? 0) ?? "HeadsetDock.PhaseIntro";
    return t("HeadsetDock.SceneStep", { phase: t(key) });
  }

  return "-";
}

/** Phase name for the progress strip — the same ranges mapWebState uses. */
export function mapPhase(t: Translate, step: number): string {
  const key = phaseKey(step);
  return key ? t(key) : "";
}
